import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { JobTitle } from '@connect-x-app/shared-domain-models';
import { SystemMessageWithType } from '@connect-x-app/shared-view-models';
import { createColumnHelper, getCoreRowModel, useReactTable } from '@tanstack/react-table';

import { TableCell, TableHeader } from '../../../../../shared';
import {
  getFormattedTimeBefore,
  getMessageTypeTranslationKey,
  getRecipientsCellValue,
} from '../../../../shared/utils/systemMessages';
import { COLUMN_CONFIG } from './systemMessagesTable.constants';

const columnHelper = createColumnHelper<SystemMessageWithType>();

export const useSystemMessageTable = (data: SystemMessageWithType[]) => {
  const { t } = useTranslation();

  const columns = useMemo(
    () => [
      columnHelper.accessor('messageType', {
        cell: props => (
          <TableCell
            {...props}
            formatter={value => {
              const { translationKey } = getMessageTypeTranslationKey(value);
              return translationKey ? t(translationKey) : value;
            }}
          />
        ),
        header: props => <TableHeader {...props} title={t('SYSTEM_MESSAGES.SYSTEM_MESSAGES_COLUMN_TYPE')} />,
        id: COLUMN_CONFIG.MESSAGE_TYPE.id,
      }),
      columnHelper.accessor('functions', {
        cell: props => {
          const recipientsArray: JobTitle[] = props.getValue();
          const { isBold, value } = getRecipientsCellValue(recipientsArray, t('SYSTEM_MESSAGES.ALL_FUNCTIONS'));
          return <TableCell {...props} formatter={() => value} textProps={isBold ? { weight: 'semiBold' } : {}} />;
        },
        header: props => <TableHeader {...props} title={t('SYSTEM_MESSAGES.SYSTEM_MESSAGES_COLUMN_RECIPIENTS')} />,
        id: COLUMN_CONFIG.FUNCTIONS.id,
        size: COLUMN_CONFIG.FUNCTIONS.size,
      }),
      columnHelper.accessor(row => row.settings?.timeBefore, {
        cell: props => {
          const value = props.getValue();
          const { color, timeBefore } = getFormattedTimeBefore(
            value,
            t('SYSTEM_MESSAGES.SYSTEM_MESSAGE_CELL_NOT_AVAILABLE_VALUE'),
          );
          return <TableCell {...props} formatter={() => timeBefore} textProps={{ color }} />;
        },
        header: props => <TableHeader {...props} title={t('SYSTEM_MESSAGES.SYSTEM_MESSAGES_COLUMN_UPDATE_TIMING')} />,
        id: COLUMN_CONFIG.SETTINGS_TIME_BEFORE.id,
        size: COLUMN_CONFIG.SETTINGS_TIME_BEFORE.size,
      }),
    ],
    [t],
  );

  return useReactTable({
    columns,
    data,
    getCoreRowModel: getCoreRowModel(),
  });
};
